/**
 * @fileoverview Helpers for building QA locators.
 * @description QA locators (`data-testid` values) are lowercase kebab-case throughout the app. Names
 * that come from data or code, such as a form field called `billingContactName`, go through here so
 * their locators follow the same pattern.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

/**
 * @description Turns a camelCase, UPPER_SNAKE_CASE or kebab-case name into the kebab-case segment of a locator.
 * @param {string} name - The name (e.g. "billingContactName", "UPDATE_STAGE" or "resend-invitation").
 * @returns {string} The kebab-case form (e.g. "billing-contact-name" or "update-stage").
 */
export function toTestIdSegment(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replaceAll('_', '-')
    .toLowerCase()
}

/**
 * @description Turns any code or key into a locator segment: lowercase, with every run of other
 * characters (colons, dots, underscores, spaces) becoming one hyphen.
 * @param {string} value - The code (e.g. "sales:my-page:view" or "PROSPECT_ASSIGNED").
 * @returns {string} The kebab-case segment (e.g. "sales-my-page-view" or "prospect-assigned").
 */
export function toTestIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}
